"use strict";
/* globals module */
/**
 * @module blogPostCommentClient
 * @description  Blogs Post Comment Client provides an easy way to consume  Blogs REST API end-points. In order to obtain needed routes `blogPostCommentClient` uses `blogPostCommentRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var common_1 = require("../../common");
var httpApi_1 = require("../../httpApi");
var _1 = require("./");
var BlogPostCommentClient = /** @class */ (function () {
    function BlogPostCommentClient(blogRoute, apiClient) {
        this.blogRoute = blogRoute;
        this.apiClient = apiClient;
        this.utility = new common_1.Utility();
    }
    Object.defineProperty(BlogPostCommentClient.prototype, "routeDefinition", {
        /**
         * Provides direct access to `blogPostCommentRoute`.
         * @method
         * @example blogPostCommentClient.routeDefinition.get();
         **/
        get: function () {
            return this.blogRoute;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of blogPostComment resources matching the given criteria.
     * @method
     * @param options A promise that is resolved once the find action has been performed.
     * @returns A promise that is resolved once the find action has been performed.
     * @example blogPostCommentClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    search : '<search-phrase>'
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
     **/
    BlogPostCommentClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns a single blogPostComment resource.
     * @method
     * @param id Blog slug or id which uniquely identifies blogPostComment resource that needs to be retrieved.
     * @param options Options object that contains embed items.
     * @returns a promise that is resolved once the get action has been performed.
     * @example blogPostCommentClient.get('<blogPostComment-id>')
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    BlogPostCommentClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.routeDefinition.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the create blogPostComment action has been performed, this action creates a new blogPostComment resource.
     * @method
     * @param data An blogPostComment object that needs to be inserted into the system.
     * @returns a promise that is resolved once the create blogPostComment action has been performed.
     * @example blogPostCommentClient.create({
                    publishDate : new Date(),
                    title : '<title>',
                    content : '<content>',
                    slug : '',
                    status : baasicBlogService.statuses.draft,
                    $tags : ['<tag1>', '<tag2>']
                })
                .then(function (data) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
     **/
    BlogPostCommentClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the update blogPostComment action has been performed; this action updates an blogPostComment resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostCommentRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(blogPostComment);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An blogPostComment object that needs to be updated into the system.
     * @returns A promise that is resolved once the update blogPostComment action has been performed.
     * @example // blogPostComment is a resource previously fetched using get action.
                    blogPostComment.title = '<title>';
                    blogPostCommentClient.update(blogPostComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostCommentClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the remove blogPostComment action has been performed. If the action is successfully completed, the blogPostComment resource will be permanently removed from the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostCommentRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(blogPostComment);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data An blogPostComment object that needs to be removed from the system.
     * @returns A promise that is resolved once the remove blogPostComment action has been performed.
     * @example // blogPostComment is a resource previously fetched using get action.
                    blogPostCommentClient.remove(blogPostComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostCommentClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    /**
     * Returns a promise that is resolved once the update blogPostComment action has been performed; this action updates an blogPostComment resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(blogPostComment);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An blogPostComment object that needs to be updated into the system.
     * @returns A promise that is resolved once the update blogPostComment action has been performed.
     * @example // blogPostComment is a resource previously fetched using get action.
                    blogPostComment.comment = '<comment>';
                    BlogPostCommentClient.approve(blogPostComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostCommentClient.prototype.approve = function (data) {
        return this.apiClient.put(this.routeDefinition.approve(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the update blogPostComment action has been performed; this action updates an blogPostComment resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(blogPostComment);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An blogPostComment object that needs to be updated into the system.
     * @returns A promise that is resolved once the update blogPostComment action has been performed.
     * @example // blogPostComment is a resource previously fetched using get action.
                    blogPostComment.comment = '<comment>';
                    BlogPostCommentClient.unapprove(blogPostComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostCommentClient.prototype.unapprove = function (data) {
        return this.apiClient.put(this.routeDefinition.unapprove(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the update blogPostComment action has been performed; this action updates an blogPostComment resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(blogPostComment);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An blogPostComment object that needs to be updated into the system.
     * @returns A promise that is resolved once the update blogPostComment action has been performed.
     * @example // blogPostComment is a resource previously fetched using get action.
                    blogPostComment.comment = '<comment>';
                    BlogPostCommentClient.spam(blogPostComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostCommentClient.prototype.spam = function (data) {
        return this.apiClient.put(this.routeDefinition.spam(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the update blogPostComment action has been performed; this action updates an blogPostComment resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(blogPostComment);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An blogPostComment object that needs to be updated into the system.
     * @returns A promise that is resolved once the update blogPostComment action has been performed.
     * @example // blogPostComment is a resource previously fetched using get action.
                    blogPostComment.comment = '<comment>';
                    BlogPostCommentClient.unspam(blogPostComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostCommentClient.prototype.unspam = function (data) {
        return this.apiClient.put(this.routeDefinition.unspam(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the update blogPostComment action has been performed; this action updates an blogPostComment resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(blogPostComment);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An blogPostComment object that needs to be updated into the system.
     * @returns A promise that is resolved once the update blogPostComment action has been performed.
     * @example // blogPostComment is a resource previously fetched using get action.
                    blogPostComment.comment = '<comment>';
                    BlogPostCommentClient.report(blogPostComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostCommentClient.prototype.report = function (data) {
        return this.apiClient.put(this.routeDefinition.report(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the update blogPostComment action has been performed; this action updates an blogPostComment resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(blogPostComment);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An blogPostComment object that needs to be updated into the system.
     * @returns A promise that is resolved once the update blogPostComment action has been performed.
     * @example // blogPostComment is a resource previously fetched using get action.
                    blogPostComment.comment = '<comment>';
                    BlogPostCommentClient.unreport(blogPostComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostCommentClient.prototype.unreport = function (data) {
        return this.apiClient.put(this.routeDefinition.unreport(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the update blogPostComment action has been performed; this action updates an blogPostComment resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(blogPostComment);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An blogPostComment object that needs to be updated into the system.
     * @returns A promise that is resolved once the update blogPostComment action has been performed.
     * @example // blogPostComment is a resource previously fetched using get action.
                    blogPostComment.comment = '<comment>';
                    BlogPostCommentClient.flag(blogPostComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostCommentClient.prototype.flag = function (data) {
        return this.apiClient.put(this.routeDefinition.flag(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the update blogPostComment action has been performed; this action updates an blogPostComment resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(blogPostComment);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An blogPostComment object that needs to be updated into the system.
     * @returns A promise that is resolved once the update blogPostComment action has been performed.
     * @example // blogPostComment is a resource previously fetched using get action.
                    blogPostComment.comment = '<comment>';
                    BlogPostCommentClient.unflag(blogPostComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostCommentClient.prototype.unflag = function (data) {
        return this.apiClient.put(this.routeDefinition.unflag(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the purge blogPostComments action has been performed. Please note that all blogPostComment resources will be deleted from the system once the action is successfully completed and therefore it can only be executed by user assigned to account owner role.
     * @method
     * @example blogPostCommentClient.purge()
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    BlogPostCommentClient.prototype.purge = function () {
        return this.apiClient.delete(this.routeDefinition.purge());
    };
    var _a, _b;
    BlogPostCommentClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.BlogPostCommentRoute)),
        tslib_1.__param(1, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.BlogPostCommentRoute !== "undefined" && _1.BlogPostCommentRoute) === "function" ? _a : Object, typeof (_b = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _b : Object])
    ], BlogPostCommentClient);
    return BlogPostCommentClient;
}());
exports.BlogPostCommentClient = BlogPostCommentClient;
/**
 * @overview
 ***Notes:**
 - Refer to the [REST API documentation](https://github.com/Baasic/baasic-rest-api/wiki) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route service.
 */ 
